/**
 * パイバル計測の永続化。
 *
 * 校正定数（k・水平線校正・標準紐長）は「クルーの手・端末・使っている風船」
 * に紐づく値なので会場に依らずグローバルに1つ持つ（declination-store.ts の
 * 磁気偏角とは違い、会場を跨いでも変わらない前提）。
 * 記録した観測（WindSample）は goals.ts / declination-store.ts と同じく
 * 会場ごとに localStorage キーを分ける。
 */

import type { WindSample } from '@gv4d/core';

const SETTINGS_KEY = 'gv4d-pibal-settings';
const SAMPLES_KEY_PREFIX = 'gv4d-pibal-samples:';

export interface PibalSettings {
  /** 校正定数 k [m²/s²]（風速² = k × tan(傾き角)） */
  k: number;
  /** 「端末を縦向きに構え、水平線を狙ったときのbeta値」[度]。null=未校正（既定90を仮定） */
  betaAtHorizon: number | null;
  /** 標準の紐の長さ [m] */
  stringLengthM: number;
}

const DEFAULT_SETTINGS: PibalSettings = { k: 30, betaAtHorizon: null, stringLengthM: 10 };

export function loadPibalSettings(): PibalSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<PibalSettings>;
    return {
      k: typeof parsed.k === 'number' && Number.isFinite(parsed.k) && parsed.k > 0 ? parsed.k : DEFAULT_SETTINGS.k,
      betaAtHorizon: typeof parsed.betaAtHorizon === 'number' && Number.isFinite(parsed.betaAtHorizon)
        ? parsed.betaAtHorizon
        : null,
      stringLengthM:
        typeof parsed.stringLengthM === 'number' && Number.isFinite(parsed.stringLengthM) && parsed.stringLengthM > 0
          ? parsed.stringLengthM
          : DEFAULT_SETTINGS.stringLengthM,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function savePibalSettings(settings: PibalSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // 保存できなくてもその場の操作は続けられる
  }
}

export function loadPibalSamples(venueId: string): WindSample[] {
  try {
    const raw = localStorage.getItem(SAMPLES_KEY_PREFIX + venueId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as WindSample[]) : [];
  } catch {
    return [];
  }
}

export function savePibalSamples(venueId: string, samples: WindSample[]): void {
  try {
    localStorage.setItem(SAMPLES_KEY_PREFIX + venueId, JSON.stringify(samples));
  } catch {
    // 保存できなくてもその場の操作は続けられる
  }
}
